<template>
  <div>
    <v-app-bar app light clipped-left elevation="0" style="border-bottom: 2px solid #ff0000!important;" color="white">
      <transition name="slide-x-transition">
        <v-app-bar-nav-icon @click="onClickMenu" v-if="sidebar">
          <v-icon v-if="drawer">mdi-backburger</v-icon>
          <v-icon v-else>mdi-menu</v-icon>
        </v-app-bar-nav-icon>
      </transition>
      <v-toolbar-title class="mr-16" @click="navigate">
        <div class="logo">idenfit</div>
      </v-toolbar-title>
      <v-toolbar-items>
        <template v-for="(item, index) in menuItems">
          <v-btn
            class="toolbar-btn"
            active-class="active-menu"
            text
            small
            depressed
            :ripple="false"
            :key="item.index"
            :to="item.to"
          >
            {{ item.text }}
          </v-btn>
          <v-divider inset class="ml-2 mr-2" vertical v-if="index < menuItems.length - 1" />
        </template>
      </v-toolbar-items>
      <v-spacer />
      <v-btn text color="primary" v-if="!terminal_status && hasWebTerminalAccess" @click="onClickClockRecord">
        <v-icon left>mdi-alarm-check</v-icon>
        {{ $t("menu.clock_in_out") }}
      </v-btn>
      <v-badge
        v-if="pendingFeedbackCount"
        :content="pendingFeedbackCount"
        :value="pendingFeedbackCount"
        color="green"
        overlap
      >
        <v-btn small icon @click="$router.push({ name: 'pending_feedbacks' })">
          <v-icon>tw-hr-feed-back</v-icon>
        </v-btn>
      </v-badge>
      <v-img
        v-if="organizationLogo"
        :src="organizationLogo"
        :lazy-src="organizationLogo"
        width="60"
        :max-width="80"
        :max-height="40"
        class="mr-3 ml-3"
      >
        <template v-slot:placeholder>
          <v-layout fill-height align-center justify-center ma-0>
            <v-progress-circular indeterminate color="grey lighten-5" />
          </v-layout>
        </template>
      </v-img>
      <v-menu left bottom transition="slide-y-transition" :nudge-width="60" :nudge-bottom="51">
        <template v-slot:activator="{ on }">
          <v-btn icon v-on="on">
            <v-avatar size="40" :color="imageUrl ? 'white' : 'primary'">
              <template v-if="imageUrl !== null">
                <v-img :src="imageUrl" :lazy-src="imageUrl">
                  <template v-slot:placeholder>
                    <v-layout fill-height align-center justify-center ma-0>
                      <v-progress-circular indeterminate color="grey lighten-5" />
                    </v-layout>
                  </template>
                </v-img>
              </template>
              <span v-else class="white--text headline"
                >{{ (firstName && firstName.substring(0, 1)) || ""
                }}{{ (lastName && lastName.substring(0, 1)) || "" }}</span
              >
            </v-avatar>
          </v-btn>
        </template>

        <v-list dense>
          <v-list-item>
            <v-list-item-avatar tile color="grey lighten-4">
              <v-img v-if="imageUrl" :src="imageUrl"></v-img>
              <v-icon v-else>mdi-account</v-icon>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title>{{ fullName }}</v-list-item-title>
              <v-list-item-subtitle>{{ organization }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-list-item @click="onClickLogout">
            <v-list-item-avatar>
              <v-progress-circular :size="32" v-if="loading" />
              <v-icon v-else>mdi-logout</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ $t("auth.logout") }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>
    <app-clock-record v-if="clockRecordDialog" @close="clockRecordDialog = false" />
  </div>
</template>

<script>
  /**
   * @project personal-idenfit
   * @developer Halil Kılıçarslan
   * @description app-header Component Logic
   * @date 28.07.2020
   */
  import { mapGetters, mapState } from "vuex";

  export default {
    name: "app-header",
    data: vm => ({
      drawer: true,
      clockRecordDialog: false,
      terminal_status: false,
      items: [
        {
          text: vm.$t("menu.dashboard"),
          to: { name: "dashboard" }
        },
        {
          text: vm.$t("menu.hr"),
          to: { name: "human_resources" },
          mobileSetting: vm.$enums.MOBILE_SETTINGS.HR_PAGE_DISABLED
        },
        {
          text: vm.$t("menu.time"),
          to: { name: "time" },
          mobileSetting: vm.$enums.MOBILE_SETTINGS.TIME_PAGE_DISABLED
        },
        {
          text: vm.$t("menu.leave"),
          to: { name: "leave" },
          mobileSetting: vm.$enums.MOBILE_SETTINGS.LEAVE_DISABLED
        }
      ]
    }),
    watch: {},
    computed: {
      menuItems() {
        return this.items.filter(item => !this.routeDisabled(item.mobileSetting));
      },
      sidebar() {
        return this.$route.matched[1].meta.sidebar;
      },
      ...mapGetters({
        loading: "auth/loading",
        firstName: "auth/firstName",
        lastName: "auth/lastName",
        fullName: "auth/fullName",
        organization: "auth/organization",
        imageUrl: "auth/imageUrl",
        organizationLogo: "auth/organizationLogo",
        routeDisabled: "auth/routeDisabled"
      }),
      ...mapState({
        isMobileSettingsFetched: state => state.auth.isMobileSettingsFetched,
        isPendingFeedbacksFetched: state => state.auth.isPendingFeedbacksFetched
      }),
      pendingFeedbackCount() {
        return this.$store.state.auth.notRequiredPendingFeedbackCount;
      },
      hasWebTerminalAccess() {
        return this.$token_manager.get("webTerminalAccess");
      }
    },
    methods: {
      navigate() {
        this.$router.push({ name: "dashboard" });
      },
      onClickLogout() {
        this.$store
          .dispatch("auth/LOGOUT")
          .then(() => {
            this.$router.replace({ name: "login" });
          })
          .catch(e => {
            this.$helpers.showNotification(e.toString());
          });
      },
      onClickMenu() {
        this.drawer = !this.drawer;
        this.$eventBus.$emit("open:sidebar", this.drawer);
      },
      onClickClockRecord() {
        this.clockRecordDialog = true;
      },
      fetchItems() {
        this.$store
          .dispatch("auth/TERMINAL_STATUS")
          .then(status => {
            this.terminal_status = status;
          })
          .catch(e => this.$helpers.showNotification(e.message));
      }
    },
    created() {
      this.fetchItems();
      if (!this.isMobileSettingsFetched) {
        this.$store
          .dispatch("auth/FETCH_MOBILE_SETTINGS")
          .then(() => {
            if (!this.isPendingFeedbacksFetched) {
              this.$store
                .dispatch("auth/FETCH_PENDING_FEEDBACKS", false)
                .then()
                .catch(console.error);
            }
          })
          .catch(console.error);
      }
      this.$eventBus.$on("close:sidebar", status => (this.drawer = status));
    }
  };
</script>

<style scoped>
  .logo {
    font-family: "Paytone One", sans-serif;
    font-size: 32px;
    font-weight: 400;
    color: var(--v-primary-base);
    letter-spacing: 0;
    cursor: pointer;
    margin-left: 0;
  }

  .toolbar-btn {
    text-transform: capitalize;
    color: #333;
  }

  .toolbar-btn:before {
    transition: none !important;
  }

  .toolbar-btn:hover:before {
    background-color: transparent;
  }

  .toolbar-btn:active:before,
  .toolbar-btn:focus:before {
    background-color: transparent;
  }

  .active-menu {
    color: var(--v-primary-base);
  }

  .active-menu:before {
    background-color: transparent;
  }
</style>
